// src/features/tower/storeReadiness/components/BranchStageSelector.tsx

import React from "react";
import type { SessionStage } from "../types";

const BRANCHES = [
  { id: "GRD", name: "Gardens" },
  { id: "PLM", name: "Plumstead" },
  { id: "PRK", name: "Parklands" },
  { id: "SEA", name: "Sea Point" },
];

const STAGES: SessionStage[] = ["OPENING", "CHANGEOVER", "CLOSING"];

type Props = {
  branchId: string;
  stage: SessionStage;
  onBranchChange: (id: string) => void;
  onStageChange: (stage: SessionStage) => void;
  allowedBranchIds?: string[] | null;
};

function stageLabel(s: SessionStage): string {
  return s.charAt(0) + s.slice(1).toLowerCase();
}

function branchName(id: string): string {
  return BRANCHES.find((b) => b.id === id)?.name ?? id;
}

export function BranchStageSelector({
  branchId,
  stage,
  onBranchChange,
  onStageChange,
  allowedBranchIds,
}: Props) {
  const isSingleBranch =
    allowedBranchIds && allowedBranchIds.length === 1;

  const visibleBranches =
    allowedBranchIds && allowedBranchIds.length > 0
      ? BRANCHES.filter((b) => allowedBranchIds.includes(b.id))
      : BRANCHES;

  // Manager locked to one branch: show a quiet label instead of buttons
  if (isSingleBranch) {
    return (
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginBottom: 16,
          fontSize: 13,
          color: "var(--ct-ink-2)",
          fontWeight: 500,
        }}
      >
        <span style={{ fontWeight: 600, color: "var(--ct-ink)" }}>
          {branchName(branchId || allowedBranchIds![0])}
        </span>
        <span style={{ color: "var(--ct-ink-3)" }}>·</span>
        <span>{stageLabel(stage)} shift</span>
      </div>
    );
  }

  // Operator: full branch buttons + stage buttons (stage auto-picks from
  // the clock but the operator can still override)
  return (
    <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 6 }}>
        {visibleBranches.map((b) => {
          const on = branchId === b.id;
          return (
            <button
              key={b.id}
              onClick={() => onBranchChange(b.id)}
              style={{
                padding: "7px 13px",
                borderRadius: 8,
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: on ? 600 : 500,
                cursor: "pointer",
                border: `1px solid ${on ? "var(--ct-accent)" : "var(--ct-line)"}`,
                background: on ? "var(--ct-accent-bg)" : "var(--ct-surface)",
                color: on ? "var(--ct-accent)" : "var(--ct-ink-2)",
              }}
            >
              {b.name}
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        {STAGES.map((s) => {
          const on = stage === s;
          return (
            <button
              key={s}
              onClick={() => onStageChange(s)}
              style={{
                padding: "7px 13px",
                borderRadius: 8,
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: on ? 600 : 500,
                cursor: "pointer",
                border: `1px solid ${on ? "var(--ct-accent)" : "var(--ct-line)"}`,
                background: on ? "var(--ct-accent-bg)" : "var(--ct-surface)",
                color: on ? "var(--ct-accent)" : "var(--ct-ink-2)",
              }}
            >
              {stageLabel(s)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
